// API Service for Prelude Backend

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'YOUR_APPS_SCRIPT_URL_HERE';

interface Song {
  id: string;
  name: string;
  artist: string;
  youtubeId: string;
  displayOrder?: number;
}

interface SongSection {
  title: string;
  songs: Song[];
}

interface ApiResponse<T> {
  success: boolean;
  error?: string;
  data?: T;
  timestamp?: string;
}

// Fetch all songs organized by sections
export async function fetchAllSongs(vendorId: string = 'default'): Promise<SongSection[]> {
  try {
    const url = `${BACKEND_URL}?action=getAllSongs&vendorId=${encodeURIComponent(vendorId)}`;
    console.log('Fetching songs from:', url);

    const response = await fetch(url);

    // Check if response is OK
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Check content type to ensure it's JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Expected JSON but got:', contentType, text.substring(0, 200));
      throw new Error('Backend returned HTML instead of JSON. Please check your Google Apps Script deployment.');
    }

    const result: ApiResponse<SongSection[]> = await response.json();

    if (result.success && result.data) {
      return result.data;
    } else {
      console.error('Failed to fetch songs:', result.error);
      return [];
    }
  } catch (error) {
    console.error('Error fetching songs:', error);
    return [];
  }
}

// Update a song's YouTube URL
export async function updateSongUrl(songId: string, youtubeId: string): Promise<boolean> {
  try {
    const url = `${BACKEND_URL}?action=updateSongUrl&songId=${encodeURIComponent(songId)}&youtubeId=${encodeURIComponent(youtubeId)}`;
    const response = await fetch(url);
    const result = await response.json();

    if (result.success) {
      console.log('Song URL updated:', result);
      return true;
    } else {
      console.error('Failed to update song URL:', result.error);
      return false;
    }
  } catch (error) {
    console.error('Error updating song URL:', error);
    return false;
  }
}

// Add a new song to a section
export async function addSong(
  sectionId: string,
  name: string,
  artist: string,
  youtubeId: string,
  vendorId: string = 'default'
): Promise<{ success: boolean; songId?: string }> {
  try {
    const url = `${BACKEND_URL}?action=addSong&sectionId=${encodeURIComponent(sectionId)}&name=${encodeURIComponent(name)}&artist=${encodeURIComponent(artist)}&youtubeId=${encodeURIComponent(youtubeId)}&vendorId=${encodeURIComponent(vendorId)}`;
    const response = await fetch(url);
    const result = await response.json();

    if (result.success) {
      return { success: true, songId: result.songId };
    } else {
      console.error('Failed to add song:', result.error);
      return { success: false };
    }
  } catch (error) {
    console.error('Error adding song:', error);
    return { success: false };
  }
}

// Delete a song
export async function deleteSong(songId: string): Promise<boolean> {
  try {
    const url = `${BACKEND_URL}?action=deleteSong&songId=${encodeURIComponent(songId)}`;
    const response = await fetch(url);
    const result = await response.json();

    return result.success;
  } catch (error) {
    console.error('Error deleting song:', error);
    return false;
  }
}

// Save client request
export async function saveClientRequest(
  vendorId: string,
  clientName: string,
  clientEmail: string,
  requestData: SongSection[]
): Promise<{ success: boolean; requestId?: string }> {
  try {
    const requestDataStr = JSON.stringify(requestData);
    const url = `${BACKEND_URL}?action=saveClientRequest&vendorId=${encodeURIComponent(vendorId)}&clientName=${encodeURIComponent(clientName)}&clientEmail=${encodeURIComponent(clientEmail)}&requestData=${encodeURIComponent(requestDataStr)}`;
    const response = await fetch(url);
    const result = await response.json();

    if (result.success) {
      return { success: true, requestId: result.requestId };
    } else {
      console.error('Failed to save request:', result.error);
      return { success: false };
    }
  } catch (error) {
    console.error('Error saving request:', error);
    return { success: false };
  }
}

// Get all sections
export async function fetchSections(vendorId: string = 'default'): Promise<any[]> {
  try {
    const url = `${BACKEND_URL}?action=getSections&vendorId=${encodeURIComponent(vendorId)}`;
    const response = await fetch(url);
    const result = await response.json();

    if (result.success) {
      return result.sections;
    } else {
      console.error('Failed to fetch sections:', result.error);
      return [];
    }
  } catch (error) {
    console.error('Error fetching sections:', error);
    return [];
  }
}

// Register a new vendor
export async function registerVendor(
  vendorName: string,
  email: string,
  contactName: string
): Promise<{ success: boolean; vendorId?: string }> {
  try {
    const url = `${BACKEND_URL}?action=registerVendor&vendorName=${encodeURIComponent(vendorName)}&email=${encodeURIComponent(email)}&contactName=${encodeURIComponent(contactName)}`;
    const response = await fetch(url);
    const result = await response.json();

    if (result.success) {
      return { success: true, vendorId: result.vendorId };
    } else {
      console.error('Failed to register vendor:', result.error);
      return { success: false };
    }
  } catch (error) {
    console.error('Error registering vendor:', error);
    return { success: false };
  }
}

// Register a new client
export async function registerClient(
  clientName: string,
  email: string
): Promise<{ success: boolean; clientId?: string }> {
  try {
    const url = `${BACKEND_URL}?action=registerClient&clientName=${encodeURIComponent(clientName)}&email=${encodeURIComponent(email)}`;
    const response = await fetch(url);
    const result = await response.json();

    if (result.success) {
      return { success: true, clientId: result.clientId };
    } else {
      console.error('Failed to register client:', result.error);
      return { success: false };
    }
  } catch (error) {
    console.error('Error registering client:', error);
    return { success: false };
  }
}

// Add a new section
export async function addSection(
  title: string,
  vendorId: string = 'default'
): Promise<{ success: boolean; sectionId?: string }> {
  try {
    const url = `${BACKEND_URL}?action=addSection&title=${encodeURIComponent(title)}&vendorId=${encodeURIComponent(vendorId)}`;
    const response = await fetch(url);
    const result = await response.json();

    if (result.success) {
      return { success: true, sectionId: result.sectionId };
    } else {
      console.error('Failed to add section:', result.error);
      return { success: false };
    }
  } catch (error) {
    console.error('Error adding section:', error);
    return { success: false };
  }
}

// Get all clients for a vendor
export async function getVendorClients(
  vendorId: string = 'default'
): Promise<any[]> {
  try {
    const url = `${BACKEND_URL}?action=getVendorClients&vendorId=${encodeURIComponent(vendorId)}`;
    const response = await fetch(url);
    const result = await response.json();

    if (result.success) {
      return result.clients;
    } else {
      console.error('Failed to fetch vendor clients:', result.error);
      return [];
    }
  } catch (error) {
    console.error('Error fetching vendor clients:', error);
    return [];
  }
}

// Get all client requests for a vendor
export async function getVendorRequests(
  vendorId: string = 'default'
): Promise<any[]> {
  try {
    const url = `${BACKEND_URL}?action=getClientRequests&vendorId=${encodeURIComponent(vendorId)}`;
    const response = await fetch(url);
    const result = await response.json();

    if (result.success) {
      return result.requests;
    } else {
      console.error('Failed to fetch vendor requests:', result.error);
      return [];
    }
  } catch (error) {
    console.error('Error fetching vendor requests:', error);
    return [];
  }
}

// Verify vendor exists by email
export async function verifyVendor(
  email: string
): Promise<{ exists: boolean; vendorId?: string; vendorName?: string; contactName?: string }> {
  try {
    const url = `${BACKEND_URL}?action=verifyVendor&email=${encodeURIComponent(email)}`;
    const response = await fetch(url);
    const result = await response.json();

    if (result.success) {
      return {
        exists: result.exists,
        vendorId: result.vendorId,
        vendorName: result.vendorName,
        contactName: result.contactName
      };
    } else {
      console.error('Failed to verify vendor:', result.error);
      return { exists: false };
    }
  } catch (error) {
    console.error('Error verifying vendor:', error);
    return { exists: false };
  }
}

// Verify client exists by email
export async function verifyClient(
  email: string
): Promise<{ exists: boolean; clientId?: string; clientName?: string; vendorId?: string }> {
  try {
    const url = `${BACKEND_URL}?action=verifyClient&email=${encodeURIComponent(email)}`;
    const response = await fetch(url);
    const result = await response.json();

    if (result.success) {
      return {
        exists: result.exists,
        clientId: result.clientId,
        clientName: result.clientName,
        vendorId: result.vendorId
      };
    } else {
      console.error('Failed to verify client:', result.error);
      return { exists: false };
    }
  } catch (error) {
    console.error('Error verifying client:', error);
    return { exists: false };
  }
}
